import { captureException } from '@sentry/browser';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSalesChannel } from 'src/components/SalesChannel/SalesChannel';
import { getField } from 'src/utils/EntryUtil';

import { Bundle, Product, fetchBundlesDetail, fetchProductsDetail } from 'src/services/dcs/bundle';

export enum DCSCacheType {
  PAGE_LEVEL = 'page-level',
  TEMPORARY = 'temporary',
}

export const TEMPORARY_CACHE_EXPIRATION_TIME = 10000;

export type BundlesMap = Partial<Record<string, Bundle>>;
export type ProductsMap = Partial<Record<string, Product>>;

export const useCachedDCSInternal = () => {
  const history = useHistory();
  const { salesChannel } = useSalesChannel();
  const resellerId = getField(salesChannel, 'cloverResellerId');

  const [bundlesMap, setBundlesMap] = useState<BundlesMap>({});
  const [bundleTimestampMap, setBundleTimestampMap] = useState({});
  const bundleRequestPendingRef = useRef({});

  const [productsMap, setProductsMap] = useState<ProductsMap>({});
  const [productTimestampMap, setProductTimestampMap] = useState({});
  const productRequestPendingRef = useRef({});

  const resetCache = useCallback(() => {
    setBundlesMap({});
    setProductsMap({});
    setBundleTimestampMap({});
    setProductTimestampMap({});
  }, []);

  useEffect(() => {
    const bundleIds = Object.keys(bundlesMap);
    const productIds = Object.keys(productsMap);

    resetCache();

    fetchBundles(bundleIds);
    fetchProducts(productIds);
  }, [resellerId]);

  useEffect(() => {
    const unregisterCallback = history.listen(resetCache);
    return unregisterCallback;
  }, []);

  const setBundleData = (bundles: Bundle[]) => {
    const [bundlesMap, bundlesTsMap] = bundles.reduce(([map, tsMap], bundle) => {
      map[bundle.id] = bundle;
      tsMap[bundle.id] = Date.now();
      return [map, tsMap];
    }, [{}, {}]);

    setBundlesMap(map => ({ ...map, ...bundlesMap }));
    setBundleTimestampMap(map => ({ ...map, ...bundlesTsMap }));
  };

  const fetchBundles = async (bundleIds: string[], cacheType: DCSCacheType = DCSCacheType.PAGE_LEVEL) => {
    if (!resellerId) return;
    const requestPendingMap = bundleRequestPendingRef.current;

    const ids = bundleIds.filter(bundleId => {
      const hasTemporaryCacheExpired = (bundleTimestampMap[bundleId] + TEMPORARY_CACHE_EXPIRATION_TIME) < Date.now();
      return (
        !requestPendingMap[bundleId] &&
        (
          (cacheType === DCSCacheType.PAGE_LEVEL && !bundlesMap[bundleId])
          ||
          (cacheType === DCSCacheType.TEMPORARY && (!bundlesMap[bundleId] || hasTemporaryCacheExpired))
        )
      );
    });

    if (!ids.length) return;

    ids.forEach(id => {
      requestPendingMap[id] = true;
    });

    try {
      const { data } = await fetchBundlesDetail({
        resellerId,
        bundleIds: ids,
      });

      setBundleData(data);
    } catch (error) {
      console.error(error);
      captureException('Failed to fetch Bundle pricing');
    } finally {
      ids.forEach(id => {
        requestPendingMap[id] = false;
      });
    }
  };

  const setProductData = (products: Product[]) => {
    const [productsMap, productsTsMap] = products.reduce(([map, tsMap], product) => {
      map[product.id] = product;
      tsMap[product.id] = Date.now();
      return [map, tsMap];
    }, [{}, {}]);

    setProductsMap(map => ({ ...map, ...productsMap }));
    setProductTimestampMap(map => ({ ...map, ...productsTsMap }));
  };

  const fetchProducts = async (productIds: string[], cacheType: DCSCacheType = DCSCacheType.PAGE_LEVEL) => {
    if (!resellerId) return;

    const requestPendingMap = productRequestPendingRef.current;

    const ids = productIds.filter(productId => {
      const hasTemporaryCacheExpired = (productTimestampMap[productId] + TEMPORARY_CACHE_EXPIRATION_TIME) < Date.now();
      return (
        !requestPendingMap[productId] &&
        (
          (cacheType === DCSCacheType.PAGE_LEVEL && !productsMap[productId])
          ||
          (cacheType === DCSCacheType.TEMPORARY && (!bundlesMap[productId] || hasTemporaryCacheExpired))
        )
      );
    });

    if (!ids.length) return;

    ids.forEach(id => {
      requestPendingMap[id] = true;
    });

    try {
      const { data } = await fetchProductsDetail({
        resellerId,
        productIds: ids,
      });

      setProductData(data);
    } catch (error) {
      console.error(error);
      captureException('Failed to fetch Product pricing');
    } finally {
      ids.forEach(id => {
        requestPendingMap[id] = false;
      });
    }
  };

  return {
    isSellerAvailable: !!resellerId, // call fetchBundles/fetchProducts only when this flag is true
    fetchBundles,
    fetchProducts,
    bundlesMap,
    productsMap,
  };
};

export type CachedDCSContext = ReturnType<typeof useCachedDCSInternal>;
